import { Route, Routes } from "react-router-dom";
import {
  ExamPeriodMonitoringPage,
  ExamPeriodCorrectionPage,
  CategoryPage,
  EmailTemplatesPage,
  NewExamPeriodPage,
  NewExamPage,
  ExamPeriodPage,
  BankPoolPage,
  ExamsPage,
  UsersPage,
  ErrorPage,
  ReportsPage,
  LevelDReportsPage,
  LevelCReportsPage,
  ReportsHubPage,
  QuizStatisticsPage,
  TournamentReportsPage,
  PendingCorrectionsPage,
  DashboardPage,
} from "@pages";
import { Suspense } from "react";
import { Flex, Spin } from "antd";
import { SignalRProvider } from "../context/SignlaRContext";
import { TenantSettings, User } from "@types";
import { getKeycloakRealm } from "./helpers";
import { QuizWizardPage } from "@/pages/quiz-wizard/QuizWizardPage";

interface RoutesConfig {
  path: string;
  element: JSX.Element;
  key: string;
}

interface Props {
  user: User;
  tenantSettings?: TenantSettings;
}

export const RoutesList = ({ user, tenantSettings }: Props) => {
  const hasAdminRole = user.applicationRoles.some((role) => role.name === "Admin");

  const routes: RoutesConfig[] = [
    {
      path: "*",
      element: (
        <Suspense fallback={<Spin />}>
          <ErrorPage />
        </Suspense>
      ),
      key: "*",
    },
  ];

  if (hasAdminRole) {
    routes.push({
      path: `/${getKeycloakRealm}/users`,
      element: <UsersPage />,
      key: "/users",
    });
    routes.push({
      path: `/${getKeycloakRealm}/categories`,
      element: <CategoryPage />,
      key: "/categories",
    });
    routes.push({
      path: `/${getKeycloakRealm}/email-templates`,
      element: <EmailTemplatesPage />,
      key: "/email-templates",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports`,
      element: <ReportsHubPage user={user} />,
      key: "/reports",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports/basic`,
      element: <ReportsPage />,
      key: "/reports/basic",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports/leveld`,
      element: <LevelDReportsPage />,
      key: "/reports/leveld",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports/levelc`,
      element: <LevelCReportsPage />,
      key: "/reports/levelc",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports/tournaments`,
      element: <TournamentReportsPage />,
      key: "/reports/tournaments",
    });
    routes.push({
      path: `/${getKeycloakRealm}/reports/quiz-statistics`,
      element: <QuizStatisticsPage user={user} />,
      key: "/reports/quiz-statistics",
    });
  }

  if (user.applicationRoles.some((role) => role.name === "QuestionAdmin")) {
    routes.push({
      path: `/${getKeycloakRealm}/bank-pool`,
      element: (
        <Suspense fallback={<Flex justify="center"><Spin /></Flex>}>
          <BankPoolPage />
        </Suspense>
      ),
      key: "/bank-pool",
    });
  }

  if (user.applicationRoles.some((role) => role.name === "TermAdmin")) {
    routes.push({
      path: `/${getKeycloakRealm}/exam-periods`,
      element: <ExamPeriodPage />,
      key: "/exam-periods",
    });
    routes.push({
      path: `/${getKeycloakRealm}/edit-exam-period/:id`,
      element: <NewExamPeriodPage />,
      key: "/exam-periods",
    });
    routes.push({
      path: `/${getKeycloakRealm}/exam-periods/new`,
      element: <NewExamPeriodPage />,
      key: "/exam-periods/new",
    });
    routes.push({
      path: `/${getKeycloakRealm}/exam-periods/new/:examId`,
      element: <NewExamPeriodPage />,
      key: "/exam-periods/new/:examId",
    });
    routes.push({
      path: `/${getKeycloakRealm}/exam-periods/monitoring/:id`,
      element: (
        <SignalRProvider>
          <ExamPeriodMonitoringPage />
        </SignalRProvider>
      ),
      key: "/exam-periods/activity",
    });
  }

  if (user.applicationRoles.some((role) => role.name && ["Assessor", "Admin", "ExamAdmin"].includes(role.name))) {
    routes.push({
      path: `/${getKeycloakRealm}/exam-periods/correction/:examInstanceId`,
      element: <ExamPeriodCorrectionPage />,
      key: "/exam-periods/correction",
    });
    routes.push({
      path: `/${getKeycloakRealm}/grading/pending-corrections`,
      element: <PendingCorrectionsPage />,
      key: "/grading/pending-corrections",
    });
  }

  if (user.applicationRoles.some((role) => role.name === "ExamAdmin")) {
    routes.push({
      path: `/${getKeycloakRealm}/`,
      element: <DashboardPage />,
      key: "/",
    });
    routes.push({
      path: `/${getKeycloakRealm}/exams`,
      element: <ExamsPage />,
      key: "/exams",
    });
    routes.push({
      path: `/${getKeycloakRealm}/new-exam`,
      element: <NewExamPage />,
      key: "/new-exam",
    });
    routes.push({
      path: `/${getKeycloakRealm}/edit-exam/:id`,
      element: <NewExamPage />,
      key: "/edit-exam/:id",
    });

    if (!tenantSettings?.disableQuizWizard) {
      routes.push({
        path: `/${getKeycloakRealm}/quiz-wizard`,
        element: <QuizWizardPage />,
        key: "/quiz-wizard",
      });
    }
  }

  const routeComponents = routes.map((element) => (
    <Route path={element.path} element={element.element} key={element.key} />
  ));

  return <Routes>{routeComponents}</Routes>;
};
